import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Container,
  Grid,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import BackBtn from "../../widgets/BackBtn";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import { saveAs } from "file-saver";
import { LoadingButton } from "@mui/lab";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

export const TEMPLATE =
  "https://betazen.s3.ap-south-1.amazonaws.com/ImportExcelSheet.xlsx";

function ImportStaff() {
  const { type } = useParams();
  const [excelData, setExcelData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");

  function excelDateToJSDate(serial) {
    const utc_days = Math.floor(serial - 25569);
    const utc_value = utc_days * 86400;
    return new Date(utc_value * 1000);
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setError(null);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        });

        if (jsonData.length <= 1) {
          setError("The uploaded file has no data rows");
          setExcelData([]);
          return;
        }

        const headers = jsonData[0];
        const dataRows = jsonData.slice(1);

        const formattedData = dataRows.map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] !== undefined ? row[index] : "";
          });
          return obj;
        });

        setExcelData(formattedData);
      } catch (err) {
        setError("Failed to process the Excel file. Please check the format.");
        console.error("File processing error:", err);
        setExcelData([]);
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError("File could not be read. Please try again.");
      setLoading(false);
      setExcelData([]);
    };

    reader.readAsBinaryString(file);
  };

  const saveList = async () => {
    if (excelData.length === 0) {
      toast.error("No data found to save!");
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_ENDPOINT}org/add-members-from-excel?type=${type}`,
        excelData,
        getHeader(ADMIN)
      );
      toast.success(data.message);
      setTimeout(() => {
        document.location.reload();
      }, 800);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    document.location.reload();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackBtn />
      <Card elevation={3}>
        <CardHeader
          title="Import Staff List"
          subheader="Upload an Excel file to import staff data"
          titleTypographyProps={{ variant: "h5", fontWeight: "bold" }}
          action={
            <Tooltip title="Download template file">
              <IconButton
                onClick={() => saveAs(TEMPLATE, "staff_import_template.xlsx")}
                color="primary"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          }
        />

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Button
                startIcon={<ImportExportIcon />}
                variant="contained"
                component="label"
                fullWidth
                sx={{ py: 1.5 }}
              >
                Choose Excel File
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  hidden
                />
              </Button>
              {fileName && (
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  Selected file: {fileName}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 1.5 }}
                onClick={() => saveAs(TEMPLATE, "staff_import_template.xlsx")}
              >
                Download Template
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              {excelData.length === 0 && !error && (
                <Paper elevation={0} sx={{ p: 3, mt: 3, bgcolor: "grey.50" }}>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon
                      color="primary"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Before you upload
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="1. Use the provided template format"
                        secondary="Ensure your Excel file matches the template structure"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="2. Include all required fields"
                        secondary="Password is mandatory for employee login"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="3. Data validation"
                        secondary="Check for empty rows or incorrect data formats"
                      />
                    </ListItem>
                  </List>
                </Paper>
              )}

              {excelData.length > 0 && (
                <>
                  <Box sx={{ mt: 4, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Data Preview
                      <Chip
                        label={`${excelData.length} records found`}
                        color="primary"
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review the data before saving to the database
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                  </Box>

                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Table
                      stickyHeader
                      aria-label="import data preview"
                      sx={{ minWidth: 650 }}
                    >
                      <TableHead>
                        <TableRow>
                          {Object.keys(excelData[0]).map((key) => (
                            <TableCell
                              key={key}
                              sx={{
                                fontWeight: "bold",
                                bgcolor: "background.paper",
                              }}
                            >
                              {key}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {excelData.slice(0, 10).map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            {Object.entries(row).map(([key, value], idx) => (
                              <TableCell key={idx}>
                                {value?.toLocaleString()}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        {excelData.length > 10 && (
                          <TableRow>
                            <TableCell
                              colSpan={Object.keys(excelData[0]).length}
                              align="center"
                              sx={{ fontStyle: "italic" }}
                            >
                              Showing 10 of {excelData.length} rows
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Please verify the data before saving. This action cannot
                      be undone.
                    </Alert>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Button
                        onClick={onCancel}
                        variant="outlined"
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        onClick={saveList}
                        loading={saving}
                        variant="contained"
                        color="primary"
                        loadingIndicator="Saving..."
                      >
                        Save {excelData.length} Records
                      </LoadingButton>
                    </Stack>
                  </Box>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default ImportStaff;
